/* cspell:ignore nics */
import i18n from '@/i18n';
import { getModelCapabilityLabel } from '@/types/model-capabilities';

import { parseImageStorageVolumesFromValues } from '../../../capabilities/maas-image-storage/imageStorageVolumes';
import { MaasRunMode } from '../../../capabilities/maas-init/maasTypes';
import { stripBillingOnlyFieldsFromMaasAnnotations } from '../../../capabilities/maas-init/utils/annotations';
import { getMaasGroupImageDisplay } from '../../../capabilities/maas-init/utils/display';
import { parseFormJsonField } from '../../../capabilities/maas-init/utils/jsonFormField';
import { normalizeModelCategory } from '../../../capabilities/maas-model-storage/modelCategory';
import { resolveModelInstancePdSeparation as resolvePdFromFormValues } from '../../../capabilities/maas-model-storage/modelFormUtils';
import type { ModelInstanceFormValues } from '../../../capabilities/maas-model-storage/modelInstanceTypes';
import { parseStorageQuantityToGi } from '../../../capabilities/maas-model-storage/quantity';
import { getDataVolumeStorageGi, getVolumeStorageClassInfo } from '../../../capabilities/maas-model-storage/storageVolume';
import { formatMaasFramework, formatMaasRunMode, formatMaasRuntimeType } from '../../../capabilities/maas-node-groups/labels';
import { resolvePrimaryDataVolumeFromValues } from '../../../capabilities/maas-node-groups/masterGroupForm';
import { collectEmbeddedNicsFromNodeFields, parseNodesField } from '../../../capabilities/maas-node-groups/nodeFields';
import { resolveUsageScenarioLabel } from '../../../capabilities/service/constants';
import {
  SERVICE_MOUNT_GROUP_NAME_FIELD,
  formatServiceMountGroupNamesDisplay,
  normalizeServiceMountGroupNames,
} from '../../../capabilities/service/serviceMountNode';
import { normalizeServiceList } from '../../../capabilities/service/utils';
import { buildPricedNicResourceConfigs, getNicIpv4CidrBlocks, getPricedNics } from '../../../components/nicPricing/utils';
import type { PipelineContext } from '../../../core/types';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import type { ResourceType } from '../../../platform/pipeline-kernel/types';
import {
  type MaasNodeGroupsSnapshot,
  appendAggregatedMaasNodeComputeDisplayRows,
  appendAggregatedStorageVolumeDisplayRow,
  appendMaasNodeComputeResourceConfigs,
  appendPerNodeSystemVolumeDisplayRows,
  appendStorageVolumeResourceConfigs,
  appendSystemVolumeResourceConfigs,
  parseMaasNodeGroups,
  toResourceConfig,
} from '../display/helpers';
import { computeTemplates, dataVolume, systemDiskSize } from '../resourceConfigs';
import { isMiModuleEnabled } from './enabledModules';
import type { MiSpec } from './schema';
import { miToForm } from './spec';

const t = i18n.t.bind(i18n);

/** PD 分离时推理节点预览分组：与下单 Prefill / Decode 勾选块 key 对齐 */
const NODE_GROUPS_PREFILL_GROUP = 'nodeGroups:prefill';
const NODE_GROUPS_DECODE_GROUP = 'nodeGroups:decode';

function pushDisplayRow(arr: DisplayDescriptor[], item: DisplayDescriptor) {
  if (!item.label || !item.value) return;
  arr.push(item);
}

function formatModelCategoryTag(categories: unknown): string | undefined {
  const labels = normalizeModelCategory(categories).map((item) => getModelCapabilityLabel(item));
  return labels.length ? labels.join('、') : undefined;
}

function getMaasComputeDisplayLabels() {
  return {
    cpu: 'CPU',
    memory: t('common:memory'),
    gpu: 'GPU',
  };
}

function specToFormValues(spec: MiSpec): ModelInstanceFormValues {
  return miToForm(spec) as ModelInstanceFormValues;
}

/** 合并 hydrate 与表单现值（下单预览依赖 form 侧 runtime / PD / 服务等字段） */
function resolveProjectionFormValues(spec: MiSpec, ctx: PipelineContext): ModelInstanceFormValues {
  const hydrated = specToFormValues(spec);
  if (!ctx.formValues) return hydrated;
  return { ...hydrated, ...ctx.formValues } as ModelInstanceFormValues;
}

function parseSegmentNodeGroups(values: ModelInstanceFormValues, segment: 'default' | 'prefill' | 'decode'): MaasNodeGroupsSnapshot {
  if (segment === 'prefill') {
    return {
      runMode: values.prefillRunMode,
      masterNodes: parseNodesField(values.prefillMasterNodes),
      workerNodes: parseNodesField(values.prefillWorkerNodes),
    };
  }
  if (segment === 'decode') {
    return {
      runMode: values.decodeRunMode,
      masterNodes: parseNodesField(values.decodeMasterNodes),
      workerNodes: parseNodesField(values.decodeWorkerNodes),
    };
  }
  return parseMaasNodeGroups(values);
}

function formatInferenceDeployModeLabel(pdSeparation: boolean): string {
  return pdSeparation ? t('data-center:maas.inference.pdSeparation') : t('data-center:maas.deploy.normal');
}

function collectInferenceEmbeddedNics(values: ModelInstanceFormValues, pdSeparation: boolean): unknown[] {
  if (!pdSeparation) {
    return collectEmbeddedNicsFromNodeFields(values);
  }
  return [
    ...collectEmbeddedNicsFromNodeFields({
      masterNodes: values.prefillMasterNodes,
      workerNodes: values.prefillWorkerNodes,
    }),
    ...collectEmbeddedNicsFromNodeFields({
      masterNodes: values.decodeMasterNodes,
      workerNodes: values.decodeWorkerNodes,
    }),
    ...collectEmbeddedNicsFromNodeFields({
      gatewayNodes: values.gatewayNodes,
    }),
  ];
}

function appendNodeGroupsDisplayRows(
  arr: DisplayDescriptor[],
  values: ModelInstanceFormValues,
  nodes: MaasNodeGroupsSnapshot,
  group: string,
  computeLabels: ReturnType<typeof getMaasComputeDisplayLabels>,
  options?: { instanceReplicas?: number; includeFramework?: boolean },
) {
  if (options?.includeFramework) {
    const embeddedSystem = nodes.workerNodes?.[0]?.system as Record<string, unknown> | undefined;
    const masterSystem = nodes.masterNodes?.[0]?.system as Record<string, unknown> | undefined;
    const inferenceSystem = embeddedSystem || masterSystem || parseFormJsonField<Record<string, unknown>>(values._inferenceSystem);
    const architecture = String(inferenceSystem?.architecture ?? '').trim();
    const frameworkLabel = formatMaasFramework(values.framework, t);
    const inferenceImage = getMaasGroupImageDisplay(inferenceSystem);
    const frameworkValue = inferenceImage || (frameworkLabel !== '-' ? frameworkLabel : '');
    if (frameworkValue) {
      pushDisplayRow(arr, {
        id: 'inference_framework',
        group,
        label: t('data-center:depend.inference.framework'),
        value: frameworkValue,
        ...(architecture ? { tag: architecture } : {}),
      });
    }
  }

  const idPrefix = group === 'nodeGroups' ? '' : `${group}_`;
  const deployModeLabel = formatMaasRunMode(nodes.runMode, t);
  if (deployModeLabel !== '-') {
    pushDisplayRow(arr, {
      id: `${idPrefix}deploy_mode`,
      group,
      label: t('data-center:maas.deploy.mode'),
      value: deployModeLabel,
    });
  }

  appendAggregatedMaasNodeComputeDisplayRows(
    (item) => pushDisplayRow(arr, { ...item, id: idPrefix ? `${idPrefix}${item.id}` : item.id, group }),
    nodes,
    computeTemplates,
    computeLabels,
    { instanceReplicas: options?.instanceReplicas },
  );
  appendPerNodeSystemVolumeDisplayRows(
    values,
    nodes,
    (item) => pushDisplayRow(arr, { ...item, id: idPrefix ? `${idPrefix}${item.id}` : item.id, group }),
    t,
  );
}

/** 缓存节点计价快照：优先用已组装的 spec.kvOffload，否则读表单节点 JSON */
function kvOffloadPricingSnapshot(spec: MiSpec, values: ModelInstanceFormValues) {
  const kv = spec.kvOffload;
  if (kv?.headGroupSpec || kv?.workerGroupSpecs?.length) {
    return {
      runMode: kv.headGroupSpec ? MaasRunMode.PrimarySecondary : MaasRunMode.Normal,
      masterNodes: kv.headGroupSpec ? [kv.headGroupSpec] : [],
      workerNodes: kv.workerGroupSpecs ?? [],
    };
  }
  const runMode = values.kvOffloadRunMode || MaasRunMode.Normal;
  const masters = parseNodesField(values.kvOffloadMasterNodes);
  const workers = parseNodesField(values.kvOffloadWorkerNodes);
  return {
    runMode,
    masterNodes: runMode === MaasRunMode.PrimarySecondary ? masters : [],
    workerNodes: workers,
  };
}

function collectKvOffloadNics(values: ModelInstanceFormValues): unknown[] {
  const nodes = [...parseNodesField(values.kvOffloadMasterNodes), ...parseNodesField(values.kvOffloadWorkerNodes)] as {
    _nicEnabled?: boolean;
    resource?: { nics?: unknown; network?: { nics?: unknown } };
  }[];
  return nodes.flatMap((node) => {
    if (node._nicEnabled === false) return [];
    const nics = node.resource?.nics ?? node.resource?.network?.nics;
    return Array.isArray(nics) ? nics : [];
  });
}

/**
 * Display 投影入口。
 * 默认路径与历史一致，供 SKU `displayConfig` 等持久化消费；
 * 仅 `previewModuleLayout: 'order-fold'` 时按勾选块增强（不影响提交 / 询价）。
 */
export function buildMiDisplayDescriptors(spec: MiSpec, ctx: PipelineContext): DisplayDescriptor[] {
  if (ctx.previewModuleLayout === 'order-fold') {
    return buildMiOrderFoldDisplayDescriptors(spec, ctx);
  }
  return buildMiPersistedDisplayDescriptors(spec, ctx);
}

/** SKU displayConfig 等持久化路径：保持既有字段与 group，禁止随 UI 预览演进。 */
function buildMiPersistedDisplayDescriptors(spec: MiSpec, ctx: PipelineContext): DisplayDescriptor[] {
  const values = specToFormValues(spec);
  const arr: DisplayDescriptor[] = [];
  const nodes = parseMaasNodeGroups(values);
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const includeModelStorage = isMiModuleEnabled('modelStorage', enabledModules, skuType);
  const includeNodeGroups = isMiModuleEnabled('nodeGroups', enabledModules, skuType);
  const includeGateway = isMiModuleEnabled('gatewayConfig', enabledModules, skuType);
  const includeKvOffload = isMiModuleEnabled('kvOffload', enabledModules, skuType);
  const includeEmbeddedNics = includeNodeGroups || includeGateway || includeKvOffload;
  const includeSystemConfig = includeNodeGroups || isMiModuleEnabled('systemConfig', enabledModules, skuType);
  const computeLabels = getMaasComputeDisplayLabels();

  if (includeModelStorage || includeSystemConfig) {
    const modelName = String(values.model?.sources?.[0]?.name || '').trim();
    const modelCategoryTag = formatModelCategoryTag(values._modelCategory);
    if (includeModelStorage && modelName) {
      pushDisplayRow(arr, {
        id: 'model_name',
        group: 'modelStorage',
        label: t('data-center:maas.service.name'),
        value: modelName,
        ...(modelCategoryTag ? { tag: modelCategoryTag } : {}),
      });
    }

    if (includeSystemConfig) {
      const embeddedSystem = (nodes as unknown as { system?: Record<string, unknown> }[])[0]?.system;
      const inferenceSystem = embeddedSystem || parseFormJsonField<Record<string, unknown>>(values._inferenceSystem);
      const architecture = String(inferenceSystem?.architecture ?? '').trim();
      const frameworkLabel = formatMaasFramework(values.framework, t);
      const inferenceImage = getMaasGroupImageDisplay(inferenceSystem);
      const frameworkValue = inferenceImage || (frameworkLabel !== '-' ? frameworkLabel : '');
      if (frameworkValue) {
        pushDisplayRow(arr, {
          id: 'inference_framework',
          group: 'nodeGroups',
          label: t('data-center:depend.inference.framework'),
          value: frameworkValue,
          ...(architecture ? { tag: architecture } : {}),
        });
      }
    }

    if (includeModelStorage) {
      const primaryDataVolume = resolvePrimaryDataVolumeFromValues(values);
      const storageClassInfo = getVolumeStorageClassInfo(primaryDataVolume);
      if (storageClassInfo) {
        pushDisplayRow(arr, {
          id: 'model_storage_class',
          group: 'modelStorage',
          label: t('common:storage.class'),
          value: storageClassInfo.alias || storageClassInfo.name || '-',
          storageClassInfo,
        });
      }
      appendAggregatedStorageVolumeDisplayRow(
        values,
        (item) => pushDisplayRow(arr, { ...item, group: 'modelStorage' }),
        dataVolume.unit || 'Gi',
        {
          resolveStorageGi: (volume, index) =>
            getDataVolumeStorageGi(volume) ?? (index === 0 ? parseStorageQuantityToGi(values.model?.sources?.[0]?.size) : undefined),
        },
      );
    }
  }

  if (includeNodeGroups) {
    const deployModeLabel = formatMaasRunMode(values.runMode, t);
    if (deployModeLabel !== '-') {
      pushDisplayRow(arr, {
        id: 'deploy_mode',
        group: 'nodeGroups',
        label: t('data-center:maas.deploy.mode'),
        value: deployModeLabel,
      });
    }

    appendAggregatedMaasNodeComputeDisplayRows(
      (item) => pushDisplayRow(arr, { ...item, group: 'nodeGroups' }),
      nodes,
      computeTemplates,
      computeLabels,
      { instanceReplicas: Number(values.replicas) },
    );
    appendPerNodeSystemVolumeDisplayRows(values, nodes, (item) => pushDisplayRow(arr, { ...item, group: 'nodeGroups' }), t);
  }

  if (includeGateway) {
    const gatewaySnapshot = {
      runMode: values.runMode,
      masterNodes: [],
      workerNodes: spec.gateway ? [spec.gateway] : parseNodesField(values.gatewayNodes),
    };
    appendAggregatedMaasNodeComputeDisplayRows(
      (item) => pushDisplayRow(arr, { ...item, group: 'gatewayConfig' }),
      gatewaySnapshot,
      computeTemplates,
      computeLabels,
    );
    appendPerNodeSystemVolumeDisplayRows(values, gatewaySnapshot, (item) => pushDisplayRow(arr, { ...item, group: 'gatewayConfig' }), t);
  }

  if (includeKvOffload) {
    const kvSnapshot = kvOffloadPricingSnapshot(spec, values);
    appendAggregatedMaasNodeComputeDisplayRows(
      (item) => pushDisplayRow(arr, { ...item, group: 'kvOffload' }),
      kvSnapshot,
      computeTemplates,
      computeLabels,
    );
    appendPerNodeSystemVolumeDisplayRows(values, kvSnapshot, (item) => pushDisplayRow(arr, { ...item, group: 'kvOffload' }), t);
  }

  if (includeEmbeddedNics) {
    const embeddedNics = [...collectEmbeddedNicsFromNodeFields(values), ...(includeKvOffload ? collectKvOffloadNics(values) : [])];
    const nicPricing = (values.network as { nicPricing?: unknown } | undefined)?.nicPricing;
    const cidrs = getPricedNics(embeddedNics as Parameters<typeof getPricedNics>[0], nicPricing).flatMap(getNicIpv4CidrBlocks);
    if (cidrs.length) {
      pushDisplayRow(arr, {
        id: 'model_network_segments',
        group: 'nodeGroups',
        label: t('data-center:network.segment.list'),
        value: cidrs.join(', '),
      });
    }
  }

  return arr;
}

/** 下单 / 确认页预览：按勾选块（含 PD Prefill·Decode）对齐，仅 UI。 */
function buildMiOrderFoldDisplayDescriptors(spec: MiSpec, ctx: PipelineContext): DisplayDescriptor[] {
  const values = resolveProjectionFormValues(spec, ctx);
  const arr: DisplayDescriptor[] = [];
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const includeRuntimeConfig = isMiModuleEnabled('runtimeConfig', enabledModules, skuType);
  const includeModelStorage = isMiModuleEnabled('modelStorage', enabledModules, skuType);
  const includeStorage = isMiModuleEnabled('storage', enabledModules, skuType);
  const includeNodeGroups = isMiModuleEnabled('nodeGroups', enabledModules, skuType);
  const includeGateway = isMiModuleEnabled('gatewayConfig', enabledModules, skuType);
  const includeKvOffload = isMiModuleEnabled('kvOffload', enabledModules, skuType);
  const includeService = isMiModuleEnabled('service', enabledModules, skuType);
  const includeEmbeddedNics = includeNodeGroups || includeGateway || includeKvOffload;
  const includeSystemConfig = includeNodeGroups || isMiModuleEnabled('systemConfig', enabledModules, skuType);
  const pdSeparation = resolvePdFromFormValues(values);
  const computeLabels = getMaasComputeDisplayLabels();

  if (includeRuntimeConfig) {
    const runtimeTypeLabel = formatMaasRuntimeType(values.runtime?.type, t);
    if (runtimeTypeLabel !== '-') {
      pushDisplayRow(arr, {
        id: 'runtime_type',
        group: 'runtimeConfig',
        label: t('data-center:maas.runtime.type'),
        value: runtimeTypeLabel,
      });
    }
    // 运行配置模块的「部署方式」= 普通部署 / PD 分离（与节点组内普通/主从区分）
    if (values._inferencePdSeparation === true || values._inferencePdSeparation === false || pdSeparation) {
      pushDisplayRow(arr, {
        id: 'inference_deploy_mode',
        group: 'runtimeConfig',
        label: t('data-center:maas.deploy.mode'),
        value: formatInferenceDeployModeLabel(pdSeparation),
      });
    }
  }

  if (includeModelStorage) {
    const modelName = String(values.model?.sources?.[0]?.name || '').trim();
    const modelCategoryTag = formatModelCategoryTag(values._modelCategory);
    if (modelName) {
      pushDisplayRow(arr, {
        id: 'model_name',
        group: 'modelStorage',
        label: t('data-center:maas.service.name'),
        value: modelName,
        ...(modelCategoryTag ? { tag: modelCategoryTag } : {}),
      });
    }

    const primaryDataVolume = resolvePrimaryDataVolumeFromValues(values);
    const storageClassInfo = getVolumeStorageClassInfo(primaryDataVolume);
    if (storageClassInfo) {
      pushDisplayRow(arr, {
        id: 'model_storage_class',
        group: 'modelStorage',
        label: t('common:storage.class'),
        value: storageClassInfo.alias || storageClassInfo.name || '-',
        storageClassInfo,
      });
    }
    appendAggregatedStorageVolumeDisplayRow(
      values,
      (item) => pushDisplayRow(arr, { ...item, group: 'modelStorage' }),
      dataVolume.unit || 'Gi',
      {
        resolveStorageGi: (volume, index) =>
          getDataVolumeStorageGi(volume) ?? (index === 0 ? parseStorageQuantityToGi(values.model?.sources?.[0]?.size) : undefined),
      },
    );
  }

  if (includeStorage) {
    parseImageStorageVolumesFromValues(values).forEach((item, index) => {
      const kindLabel = item.kind === 'ephemeral' ? t('common:ephemeral.volume') : t('common:persistent.volume');
      pushDisplayRow(arr, {
        id: `image_storage_${index}`,
        group: 'storage',
        label: t('data-center:maas.storage.configure.index', { index: index + 1 }),
        value: `${item.mountPath}（${kindLabel}）`,
      });
    });
  }

  if (includeNodeGroups) {
    if (pdSeparation) {
      appendNodeGroupsDisplayRows(arr, values, parseSegmentNodeGroups(values, 'prefill'), NODE_GROUPS_PREFILL_GROUP, computeLabels, {
        instanceReplicas: Number(values.prefillReplicas),
        includeFramework: includeSystemConfig,
      });
      appendNodeGroupsDisplayRows(arr, values, parseSegmentNodeGroups(values, 'decode'), NODE_GROUPS_DECODE_GROUP, computeLabels, {
        instanceReplicas: Number(values.decodeReplicas),
      });
    } else {
      appendNodeGroupsDisplayRows(arr, values, parseSegmentNodeGroups(values, 'default'), 'nodeGroups', computeLabels, {
        instanceReplicas: Number(values.replicas),
        includeFramework: includeSystemConfig,
      });
    }
  }

  if (includeGateway) {
    const gatewaySnapshot = {
      runMode: values.runMode,
      masterNodes: [],
      workerNodes: spec.gateway ? [spec.gateway] : parseNodesField(values.gatewayNodes),
    };
    appendAggregatedMaasNodeComputeDisplayRows(
      (item) => pushDisplayRow(arr, { ...item, group: 'gatewayConfig' }),
      gatewaySnapshot,
      computeTemplates,
      computeLabels,
    );
    appendPerNodeSystemVolumeDisplayRows(values, gatewaySnapshot, (item) => pushDisplayRow(arr, { ...item, group: 'gatewayConfig' }), t);
  }

  if (includeKvOffload) {
    const kvSnapshot = kvOffloadPricingSnapshot(spec, values);
    appendAggregatedMaasNodeComputeDisplayRows(
      (item) => pushDisplayRow(arr, { ...item, group: 'kvOffload' }),
      kvSnapshot,
      computeTemplates,
      computeLabels,
    );
    appendPerNodeSystemVolumeDisplayRows(values, kvSnapshot, (item) => pushDisplayRow(arr, { ...item, group: 'kvOffload' }), t);
  }

  if (includeService) {
    const services = normalizeServiceList(values._inferenceService ?? spec.network?.service);
    services.forEach((svc, index) => {
      const name = String(svc.name || '').trim() || t('data-center:access.config.service.endpoint');
      const scenario = resolveUsageScenarioLabel(svc.usageScenario);
      const internalPort = svc.internalPort != null ? String(svc.internalPort) : '-';
      const path = String(svc.routerPath || '').trim() || '-';
      pushDisplayRow(arr, {
        id: `service_${index}`,
        group: 'service',
        label: name,
        value: `${scenario} / ${t('common:internal.port')} ${internalPort} / ${t('common:path')} ${path}`,
      });
    });
    const mountNames = normalizeServiceMountGroupNames(values[SERVICE_MOUNT_GROUP_NAME_FIELD as keyof ModelInstanceFormValues]);
    const mountDisplay = formatServiceMountGroupNamesDisplay(mountNames, '');
    if (mountDisplay) {
      pushDisplayRow(arr, {
        id: 'service_mount_nodes',
        group: 'service',
        label: t('data-center:maas.service.entry.nodes'),
        value: mountDisplay,
      });
    }
  }

  if (includeEmbeddedNics) {
    const embeddedNics = [
      ...collectInferenceEmbeddedNics(values, pdSeparation && includeNodeGroups),
      ...(includeKvOffload ? collectKvOffloadNics(values) : []),
    ];
    const nicPricing = (values.network as { nicPricing?: unknown } | undefined)?.nicPricing;
    const cidrs = getPricedNics(embeddedNics as Parameters<typeof getPricedNics>[0], nicPricing).flatMap(getNicIpv4CidrBlocks);
    if (cidrs.length) {
      pushDisplayRow(arr, {
        id: 'model_network_segments',
        group: pdSeparation && includeNodeGroups ? NODE_GROUPS_PREFILL_GROUP : 'nodeGroups',
        label: t('data-center:network.segment.list'),
        value: cidrs.join(', '),
      });
    }
  }

  return arr;
}

/** 询价 / SKU 资源投影：保持既有节点字段语义，不随展示侧 PD 分组或 formValues 合并改动。 */
export function buildMiResourceConfigs(spec: MiSpec, ctx: PipelineContext): ResourceType[] {
  const values = specToFormValues(spec);
  const nodes = parseMaasNodeGroups(values);
  const result: ResourceType[] = [];
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const includeModelStorage = isMiModuleEnabled('modelStorage', enabledModules, skuType);
  const includeNodeGroups = isMiModuleEnabled('nodeGroups', enabledModules, skuType);
  const includeGateway = isMiModuleEnabled('gatewayConfig', enabledModules, skuType);
  const includeKvOffload = isMiModuleEnabled('kvOffload', enabledModules, skuType);
  if (includeNodeGroups) {
    // 算力 = 节点组副本 × 拓扑副本（replicas）；系统盘/共享卷/网关/缓存不乘拓扑副本
    appendMaasNodeComputeResourceConfigs(result, nodes, computeTemplates, toResourceConfig, {
      instanceReplicas: Number(values.replicas),
    });
    appendSystemVolumeResourceConfigs(result, systemDiskSize, values, nodes, toResourceConfig);
  }
  if (includeGateway) {
    const gatewaySnapshot = {
      runMode: values.runMode,
      masterNodes: [],
      workerNodes: spec.gateway ? [spec.gateway] : parseNodesField(values.gatewayNodes),
    };
    appendMaasNodeComputeResourceConfigs(result, gatewaySnapshot, computeTemplates, toResourceConfig);
    appendSystemVolumeResourceConfigs(result, systemDiskSize, values, gatewaySnapshot, toResourceConfig);
  }
  if (includeKvOffload) {
    const kvSnapshot = kvOffloadPricingSnapshot(spec, values);
    appendMaasNodeComputeResourceConfigs(result, kvSnapshot, computeTemplates, toResourceConfig);
    appendSystemVolumeResourceConfigs(result, systemDiskSize, values, kvSnapshot, toResourceConfig);
  }
  if (includeNodeGroups || includeGateway || includeKvOffload) {
    const embeddedNics = [...collectEmbeddedNicsFromNodeFields(values), ...(includeKvOffload ? collectKvOffloadNics(values) : [])];
    if (embeddedNics.length) {
      result.push(
        ...buildPricedNicResourceConfigs(embeddedNics as Parameters<typeof buildPricedNicResourceConfigs>[0], spec.network?.nicPricing),
      );
    }
  }

  if (includeModelStorage) {
    appendStorageVolumeResourceConfigs(result, dataVolume, values, toResourceConfig, {
      resolveStorageGi: (volume, index) =>
        getDataVolumeStorageGi(volume) ?? (index === 0 ? parseStorageQuantityToGi(values.model?.sources?.[0]?.size) : undefined),
    });
  }

  return result;
}

export function pickMiPresetFields(values: Record<string, unknown>): Record<string, unknown> {
  return values;
}

/** 直建 / 下单 apiBody：剥离询价专用 annotations 与网卡计价字段。 */
export function serializeMiApiBody(spec: MiSpec, _ctx: PipelineContext): Record<string, unknown> {
  const annotations = stripBillingOnlyFieldsFromMaasAnnotations(spec.annotations);
  const { nicPricing: _nicPricing, ...apiNetwork } = spec.network || {};
  if (_nicPricing && apiNetwork.nics?.length) {
    apiNetwork.nics = getPricedNics(apiNetwork.nics, _nicPricing);
  }
  const network = Object.values(apiNetwork).some((value) => value !== undefined) ? apiNetwork : undefined;
  return {
    ...(spec as Record<string, unknown>),
    network,
    ...(annotations ? { annotations } : { annotations: undefined }),
  };
}
